import PageLayout from '../../components/PageLayout/PageLayout';
import chargers from '../../assets/sideImage.jpg';
import {
  MethodologyWrapper,
  MethodologyTitle,
  MethodologySubtitle,
  MethodologySection,
  SectionTitle,
  SectionBody,
  SectionList,
  MethodologyLabel,
  OrderedList,
  StoplightGreen,
  StoplightYellow,
  StoplightRed,
} from './Methodology.styled';

const Methodology = () => (
  <PageLayout
    page="methodology"
    sideImage={{ src: chargers, alt: 'Image of EV Chargers' }}
  >
    <MethodologyWrapper>
      <MethodologyTitle>Methodology</MethodologyTitle>
      <MethodologySubtitle>
        This tool helps employers estimate the costs, revenue, and employee
        benefit of installing EV workplace charging. Results are based on your
        inputs combined with publicly sourced cost, energy rate, and emissions
        data. Use this page to understand how those estimates are calculated and
        what assumptions are built in.
      </MethodologySubtitle>

      <MethodologySection>
        <SectionTitle>Equipment &amp; Installation Costs</SectionTitle>
        <SectionBody>
          <p>
            One-time cost estimates are derived from data on over 700 Level 2
            charging installation projects. Costs are organized by state and
            installation labor is adjusted using state-level electrician wage
            data from the Bureau of Labor Statistics (BLS). This ensures that
            estimates reflect local market conditions rather than a national
            average.
          </p>
        </SectionBody>
      </MethodologySection>

      <MethodologySection>
        <SectionTitle>Ongoing Operating Costs</SectionTitle>
        <SectionBody>
          <p>
            Three recurring annual costs are factored into your net yearly
            position:
          </p>
        </SectionBody>
        <SectionList>
          <li>
            <MethodologyLabel>Electricity:</MethodologyLabel> Your state&apos;s
            commercial electricity rate multiplied by the estimated annual kWh
            consumed at the chargers. All electricity rates are sourced from
            U.S. Energy Information Administration (EIA):{' '}
            <a href="https://www.eia.gov/">https://www.eia.gov/</a>.
          </li>
          <li>
            <MethodologyLabel>Operations &amp; Maintenance:</MethodologyLabel>{' '}
            Estimated at 10% of installation cost per year for L2 Networked
            chargers and 5% for others, covering routine upkeep, network fees
            (for networked chargers), and minor repairs.
          </li>
          <li>
            <MethodologyLabel>Financing:</MethodologyLabel> Estimated at 3% of
            total upfront costs (hardware + installation) per year, representing
            the cost of capital for financing the project.
          </li>
        </SectionList>
      </MethodologySection>

      <MethodologySection>
        <SectionTitle>Charging Revenue &amp; Rate Scenarios</SectionTitle>
        <SectionBody>
          <p>
            The tool models four pricing strategies across a range of per-kWh
            rates so you can see how your pricing decision affects both revenue
            and employee participation:
          </p>
        </SectionBody>
        <SectionList>
          <li>
            <MethodologyLabel>Free ($0/kWh):</MethodologyLabel> Highest
            utilization and employee satisfaction. No charging revenue, but
            maximizes amenity value.
          </li>
          <li>
            <MethodologyLabel>
              Subsidized (below residential rate):
            </MethodologyLabel>{' '}
            Reduces your electricity cost partially. Strong adoption, employees
            still save compared to charging elsewhere.
          </li>
          <li>
            <MethodologyLabel>
              Cost Recovery (at residential rate):
            </MethodologyLabel>{' '}
            Breaks even on electricity costs. Moderate utilization, employees
            pay roughly what they would at home.
          </li>
          <li>
            <MethodologyLabel>
              Full Recovery (above residential rate):
            </MethodologyLabel>{' '}
            Generates net revenue. Lower utilization, higher prices reduce how
            frequently employees plug in.
          </li>
        </SectionList>
        <SectionBody>
          <p style={{ marginTop: '0.75rem' }}>
            Charging revenue equals the fee ($/kWh) multiplied by annual energy
            consumed (kWh). Annual energy varies based on the pricing strategy
            selected, the number of EV drivers in the workplace, and whether
            they have access to home charging.
          </p>
        </SectionBody>
      </MethodologySection>

      <MethodologySection>
        <SectionTitle>Employee Amenity Value</SectionTitle>
        <SectionBody>
          <p>
            A stoplight indicator <StoplightGreen>Green</StoplightGreen> /{' '}
            <StoplightYellow>Yellow</StoplightYellow> /{' '}
            <StoplightRed>Red</StoplightRed> reflects how employees are likely
            to perceive the charging program. The score is based on three
            factors:
          </p>
        </SectionBody>
        <SectionList>
          <li>
            <MethodologyLabel>Pricing:</MethodologyLabel> Free and subsidized
            charging scores highest. Full-recovery pricing reduces the perceived
            benefit.
          </li>
          <li>
            <MethodologyLabel>Availability:</MethodologyLabel> Based on the
            ratio of EV commuters to total chargers (existing + new). Ratios of
            five to ten EVs per charger reduce the amenity value slightly.
            Ratios greater than ten Evs per charger greatly reduce the amenity
            value.
          </li>
          <li>
            <MethodologyLabel>Access:</MethodologyLabel> Programs restricted to
            employees score higher than those open to the public, since
            availability for employees is more reliable.
          </li>
        </SectionList>
      </MethodologySection>

      <MethodologySection>
        <SectionTitle>Environmental Impact</SectionTitle>
        <SectionBody>
          <p>
            GHG emissions avoided are estimated by comparing the emissions from
            gasoline miles that are replaced by electricity charged at the
            workplace. The calculation follows these steps:
          </p>
        </SectionBody>
        <OrderedList>
          <li>Estimate annual kWh charged at the workplace.</li>
          <li>
            Convert to miles driven using an efficiency of{' '}
            <MethodologyLabel>3.5 miles/kWh</MethodologyLabel>.
          </li>
          <li>
            Calculate avoided tailpipe emissions: miles ÷ 30 MPG x 8.887 kg CO₂
            per gallon.
          </li>
          <li>
            Subtract upstream grid emissions: kWh x{' '}
            <a href="https://www.epa.gov/egrid">
              your state&apos;s eGrid 2023 emission factor
            </a>{' '}
            (kg CO₂/kWh).
          </li>
          <li>
            Report result in{' '}
            <MethodologyLabel>tons of CO₂ avoided per year</MethodologyLabel>,
            rounded to the nearest 10 tons.
          </li>
        </OrderedList>
      </MethodologySection>

      <MethodologySection>
        <SectionTitle>Incentives</SectionTitle>
        <SectionBody>
          <p>
            On the inputs page you can enter expected incentive amounts these
            are applied directly as reductions to your one-time capital costs.
            Because eligibility rules vary by program and incentives often
            cannot be stacked or combined, you should verify applicable amounts
            with the incentive provider before using them in financial planning.
          </p>
        </SectionBody>
      </MethodologySection>

      <MethodologySection>
        <SectionTitle>Key Assumptions</SectionTitle>
        <SectionList>
          <li>
            <MethodologyLabel>EV demand:</MethodologyLabel> Estimated from your
            inputs as: Employees x average daily attendance % x car commuter % x
            EV share %.
          </li>
          <li>
            <MethodologyLabel>Operating weeks:</MethodologyLabel> 48 weeks per
            year (accounting for holidays and low-occupancy periods).
          </li>
          <li>
            <MethodologyLabel>Home charging access:</MethodologyLabel> Employees
            with home charging use workplace chargers less frequently than those
            without reflected in per-rate utilization estimates.
          </li>
          <li>
            <MethodologyLabel>Utilization ramp-up:</MethodologyLabel> The model
            assumes chargers reach steady-state utilization immediately after
            installation. Actual ramp-up time may vary.
          </li>
          <li>
            <MethodologyLabel>Daily sessions per port:</MethodologyLabel> Assume
            L2 networked ports can support up to two daily charging sessions,
            while L2 or L1 non-networked ports can only support one daily
            charging session.
          </li>
          <li>
            <MethodologyLabel>Energy per session cap:</MethodologyLabel> Level 1
            ports are capped at delivering 11 kWh per day.
          </li>
          <li>
            <MethodologyLabel>Charging-as-a-Service:</MethodologyLabel> CaaS
            monthly fee is estimated by first calculating the cost under direct
            ownership and then applying cost recovery assumptions. Notably, we
            assume that 50% of the installation costs and all hardware costs are
            included in the monthly fee and that the CaaS provider assumes a
            five year customer tenure. Other assumptions include six year
            equipment life and 16.5% cost of capital.
          </li>
        </SectionList>
      </MethodologySection>
    </MethodologyWrapper>
  </PageLayout>
);

export default Methodology;
